<script setup lang="ts">
    import { computed, watch, nextTick, toRef } from 'vue'

    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart'
    import { getDistinctColor, numCompare, roundDecimal, splitString } from '@common/Common'

    import type {
        WeightsViewerCriterionReadModel,
        WeightsViewerReadModel
    } from '@Criteria/Weights/Domain/readmodels'

    // ---------------- local jquery ----------------

    const $ = ((window as any).kendo?.jQuery ?? (window as any).$) as JQueryStatic

    // ---------------- props / emits ----------------

    const props = defineProps<{
        propOpened: boolean
        propRm: WeightsViewerReadModel | null
    }>()

    const emit = defineEmits<{
        (e: 'export-weightsset-weights', id: number): void
        (e: 'export-survey-weights', id: number): void
        (e: 'export-panelist-weights', id: number): void
        (e: 'close-dialog'): void
    }>()

    // ---------------- shortcuts ----------------

    const opened = toRef(props, 'propOpened')
    const rm = toRef(props, 'propRm')

    // ---------------- local state ----------------

    let PieChart: KendoChart | null = null
    let BarChart: KendoChart | null = null

    // ---------------- computed ----------------

    const selectedCriterionId = computed(() => rm.value?.SelectedCriterionId)

    const selectedCriterion = computed<WeightsViewerCriterionReadModel | null>(() =>
        rm.value?.Criteria.find(c => c.Id === selectedCriterionId.value) ?? null
    )

    const dataVizType = computed(() => rm.value?.DataVizType ?? null)
    const precision = computed(() => rm.value?.Precision ?? null)

    const precisionLabel = computed(() =>
        precision.value != null
            ? `${1 / Math.pow(10, precision.value)}%`
            : ''
    )

    const Weights = computed(() => rm.value?.Criteria ?? [])

    const Parents = computed(() =>
        Weights.value.filter(a => a.IsParent === true)
    )

    const Subcriteria = computed(() =>
        Weights.value.filter(a => a.ParentId === selectedCriterionId.value)
    )

    const Bottom = computed(() =>
        GetChildlessDescendantCriteria(selectedCriterionId.value!, Weights.value)
    )

    // ---------------- watchers (replaces @Component.watch) ----------------

    watch(selectedCriterion, val =>
    {
        if (val)
        {
            nextTick(RenderDataViz)
        }
    })

    watch(precision, (_, oldVal) =>
    {
        if (oldVal != null)
        {
            RenderDataViz()
        }
    })

    watch(dataVizType, (_, oldVal) =>
    {
        if (oldVal != null)
        {
            nextTick(RenderDataViz)
        }
    })

    // ---------------- helpers ----------------

    function getLocalWeight(w: WeightsViewerCriterionReadModel): number
    {
        return roundDecimal(w.LocalWeight, precision.value!)
    }

    function getGlobalWeight(w: WeightsViewerCriterionReadModel): number
    {
        return roundDecimal(w.GlobalWeight, precision.value!)
    }

    function getLocalWeightLabel(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(`${w.Name} (${getLocalWeight(w)}%)`, 30).join('\n')
    }

    function getGlobalWeightLabel(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(`${w.Name} (${getGlobalWeight(w)}%)`, 30).join('\n')
    }

    function getCriterionNameMultiline(w: WeightsViewerCriterionReadModel): string
    {
        return splitString(w.Name, 40).join('\n')
    }

    // ---------------- actions ----------------

    function SetCriterion(id: number)
    {
        rm.value!.SelectedCriterionId = id
    }

    function SetPrecision(p: number)
    {
        rm.value!.Precision = p
    }

    function SwitchToChart()
    {
        rm.value!.DataVizType = 'Chart'
    }

    function SwitchToGrid()
    {
        rm.value!.DataVizType = 'Grid'
    }

    // ---------------- grid ----------------

    function GetGridData(contextId: number)
    {
        return Weights.value
            .filter(a => a.ParentId === contextId)
            .map(w => ({
                CriterionId: w.Id,
                Name: w.Name,
                LocalWeight: getLocalWeight(w),
                LocalWeightProgressBarValue: w.LocalWeightProgressBarValue,
                GlobalWeight: getGlobalWeight(w),
                GlobalWeightProgressBarValue: w.GlobalWeightProgressBarValue,
                HasChildren: w.IsParent
            }))
    }

    function RenderGrid()
    {
        const data = GetGridData(selectedCriterionId.value!)

        const grid = $('#grid').data('kendoGrid')
        if (grid) grid.destroy()

        $('#grid').kendoGrid({
            dataSource: { data },
            height: 600,
            detailInit: e => GridDetailInit(e, GetGridData),
            dataBound: GridDataBound,
            resizable: true,
            sortable: true,
            columnMenu: true,
            filterable: true,
            columns: [
                { field: 'Name', title: 'Criterion', width: '220px' },
                { field: 'LocalWeight', title: 'Local Weight', width: '220px', template: GetLocalWeightColumnTemplate },
                { field: 'GlobalWeight', title: 'Global Weight', width: '220px', template: GetGlobalWeightColumnTemplate }
            ]
        })
    }

    // ---------------- charts ----------------

    function RenderPieChart()
    {
        const data = Subcriteria.value.map((w, i) => ({
            CriterionId: w.Id,
            Name: w.Name,
            Weight: getLocalWeight(w),
            Label: getLocalWeightLabel(w),
            Color: getDistinctColor(i)
        }))

        const options = new KendoChartOptions()
        options.SetTooltip(true, '#= category #: #= value #%')
        options.HideLegend()
        options.SetDonutSeriesType()

        const serie = new KendoChartDataSerie()
        serie.SetField('Weight')
        serie.SetCategoryField('Name')
        serie.SetColorField('Color')
        serie.SetLabels(true, '#= dataItem.Label #', '1.2rem Arial', 'outsideEnd')
        serie.SetLabelsAlign('circle')
        serie.EnableAutoFit()
        serie.SetData(data)

        options.SetOnSeriesClick(e =>
        {
            if (Weights.value.some(a => a.ParentId === e.dataItem.CriterionId))
            {
                SetCriterion(e.dataItem.CriterionId)
            }
        })

        PieChart = KendoChart.CreateChart(options)
        PieChart.SetDataSeries([serie])
        PieChart.Render1('PieChart')
    }

    function RenderBarChart()
    {
        const data = Subcriteria.value
            .flatMap((w, i) =>
            {
                const bottom = GetChildlessDescendantCriteria(w.Id, Weights.value)
                const list = bottom.length ? bottom : [w]

                return list.map(b => ({
                    CriterionId: b.Id,
                    Name: b.Name,
                    MultilineName: getCriterionNameMultiline(b),
                    Weight: getGlobalWeight(b),
                    Label: getGlobalWeightLabel(b),
                    Color: getDistinctColor(i)
                }))
            })
            .sort((a, b) => numCompare(a.Weight, b.Weight))
            .reverse()

        const options = new KendoChartOptions()
        options.SetTooltip(true, '#= dataItem.Name #: #= value #%')
        options.HideLegend()
        options.SetBarSeriesType()
        options.SetCategoryAxis(true, true, '1rem Arial')

        const serie = new KendoChartDataSerie()
        serie.SetField('Weight')
        serie.SetCategoryField('MultilineName')
        serie.SetColorField('Color')
        serie.SetData(data)

        BarChart = KendoChart.CreateChart(options)
        BarChart.SetDataSeries([serie])

        BarChart.SetHeight(Math.max(data.length * 100, 300))
        BarChart.Render1('BarChart')
    }

    function RenderDataViz()
    {
        if (dataVizType.value === 'Chart')
        {
            RenderPieChart()
            RenderBarChart()
        }
        else if (dataVizType.value === 'Grid')
        {
            RenderGrid()
        }
    }

    // ---------------- export / close ----------------

    function exportWeights()
    {
        if (rm.value?.WeightsSetId)
            emit('export-weightsset-weights', rm.value.WeightsSetId)
        else if (rm.value?.SurveyId)
            emit('export-survey-weights', rm.value.SurveyId)
        else if (rm.value?.ProjectUserId)
            emit('export-panelist-weights', rm.value.ProjectUserId)
    }

    function close()
    {
        emit('close-dialog')
    }

    // ---------------- pure helpers (unchanged logic) ----------------

    function GetChildlessDescendantCriteria(
        criterionId: number,
        all: WeightsViewerCriterionReadModel[]
    )
    {
        return GetDescendantCriteria(criterionId, all).filter(a => !a.IsParent)
    }

    function GetDescendantCriteria(
        criterionId: number,
        all: WeightsViewerCriterionReadModel[]
    ): WeightsViewerCriterionReadModel[]
    {
        const subs = all.filter(a => a.ParentId === criterionId)
        return subs.flatMap(s => [s, ...GetDescendantCriteria(s.Id, all)])
    }

    function GridDetailInit(e: any, getData: (id: number) => any[])
    {
        $('<div/>').appendTo(e.detailCell).kendoGrid({
            dataSource: { data: getData(e.data.CriterionId) },
            detailInit: ev => GridDetailInit(ev, getData),
            dataBound: GridDataBound,
            resizable: true,
            sortable: true,
            columnMenu: true,
            filterable: true,
            columns: [
                { field: 'Name', title: 'Criterion' },
                { field: 'LocalWeight', title: 'Local Weight', width: '200px', template: GetLocalWeightColumnTemplate },
                { field: 'GlobalWeight', title: 'Global Weight', width: '200px', template: GetGlobalWeightColumnTemplate }
            ]
        })
    }

    function GridDataBound(this: any)
    {
        const data = this.dataSource.data()
        for (const row of data)
        {
            if (row.get('HasChildren') === false)
            {
                $(`tr[data-uid="${row.uid}"]`).find('.k-hierarchy-cell a').hide()
            }
        }
    }

    function GetLocalWeightColumnTemplate(e: any): string
    {
        const value = e.LocalWeight
        const progress = e.LocalWeightProgressBarValue
        const textColorCss = progress < 10 ? 'text-dark' : ''

        return `<div class="progress" style="height:26px">
        <div class="progress-bar ${textColorCss}" style="width:${progress}%">${value}</div>
    </div>`
    }

    function GetGlobalWeightColumnTemplate(e: any): string
    {
        const value = e.GlobalWeight
        const progress = e.GlobalWeightProgressBarValue
        const textColorCss = progress < 10 ? 'text-dark' : ''

        return `<div class="progress" style="height:26px">
        <div class="progress-bar ${textColorCss}" style="width:${progress}%">${value}</div>
    </div>`
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable" style="max-width: 96%;">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Weights: {{rm?.Name}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="rm">
                            <div class="d-flex justify-content-between">
                                <div class="dropdown">
                                    <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        <span>{{selectedCriterion?.Name}}</span>
                                    </button>
                                    <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                        <a class="dropdown-item" v-for="c in Parents" :key="c.Id" @click="SetCriterion(c.Id)">
                                            <span>{{c.Name}}</span><br />
                                            <small>{{c.Path}}</small>
                                        </a>
                                    </div>
                                </div>
                                <div>
                                    <div class="dropdown d-inline-block">
                                        <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">{{precisionLabel}}</button>
                                        <div class="dropdown-menu">
                                            <h6 class="dropdown-header">Precision:</h6>
                                            <a class="dropdown-item" @click="SetPrecision(0)">1</a>
                                            <a class="dropdown-item" @click="SetPrecision(1)">0.1</a>
                                            <a class="dropdown-item" @click="SetPrecision(2)">0.01</a>
                                            <a class="dropdown-item" @click="SetPrecision(3)">0.001</a>
                                            <a class="dropdown-item" @click="SetPrecision(4)">0.0001</a>
                                            <a class="dropdown-item" @click="SetPrecision(5)">0.00001</a>
                                        </div>
                                    </div>

                                    <div class="btn-group" role="group">
                                        <button type="button" class="btn btn-outline-secondary" @click="SwitchToChart">
                                            <i class="fa fa-pie-chart" aria-hidden="true"></i>
                                        </button>
                                        <button type="button" class="btn btn-outline-secondary" @click="SwitchToGrid">
                                            <i class="fa fa-table" aria-hidden="true"></i>
                                        </button>
                                    </div>

                                    <button type="button" class="btn btn-outline-secondary" @click="exportWeights">
                                        <i class="fa fa-download fa-lg"></i>
                                    </button>
                                </div>
                            </div>

                            <br />
                            <div v-show="dataVizType === 'Chart'">
                                <div id="Chart-PieChart"></div>
                                <div id="Chart-BarChart"></div>
                            </div>
                            <div v-show="dataVizType === 'Grid'">
                                <div id="grid"></div>
                            </div>
                            <br />
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
